import {
    Directive,
    effect,
    inject,
    input,
    Input,
    TemplateRef,
    ViewContainerRef,
} from "@angular/core";
import { Observable, of, Subscription } from "rxjs";
import { LoadingComponent } from "./loading.component";

/**
 * Shows a loading spinner until the observable has emitted. Once it has, if the emitted
 * value is truthy show the content, otherwise optionally show an alternative template
 */
@Directive({
    selector: "[appAsyncIf]",
})
export class AsyncIfDirective<T> {
    private subscription?: Subscription;
    private isLoading = false;

    public readonly appAsyncIfOf = input.required<Observable<T> | T>();
    public readonly appAsyncIfElse = input<TemplateRef<unknown>>();

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("appAsyncIfElse") public elseTemplateRef?: TemplateRef<unknown>;

    private readonly templateRef: TemplateRef<{ $implicit: T }> = inject(TemplateRef);
    private readonly viewContainer = inject(ViewContainerRef);

    public constructor() {
        effect((onCleanUp) => {
            this.renderView();
            onCleanUp(() => this.subscription?.unsubscribe());
        });
    }

    private renderView() {
        // If a second observable is set before the first
        this.subscription?.unsubscribe();

        if (!this.isLoading) {
            this.isLoading = true;
            this.viewContainer.clear();
            this.viewContainer.createComponent(LoadingComponent);
        }

        let value$ = this.appAsyncIfOf();
        if (!(value$ instanceof Observable)) {
            value$ = of(value$);
        }

        // Use a sentinal value so a first emit of undefined doesn't early exit below
        let previousValue: T | object = new Object();
        this.subscription = value$.subscribe((v) => {
            this.isLoading = false;

            if (previousValue === v) {
                return;
            }

            this.viewContainer.clear();

            if (v) {
                this.viewContainer.createEmbeddedView(this.templateRef, {
                    $implicit: v,
                });
            } else if (this.elseTemplateRef) {
                this.viewContainer.createEmbeddedView(this.elseTemplateRef);
            }

            previousValue = v;
        });
    }

    static ngTemplateContextGuard<T>(
        _dir: AsyncIfDirective<T>,
        ctx: unknown,
    ): ctx is { $implicit: Exclude<T, false | 0 | "" | null | undefined> } {
        return true;
    }
}
